import { FC } from 'react';

import { useFormik } from 'formik';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';

import { SECOND_SPIN_DESCRIPTION_MAX_LENGTH, SpecialDropSettingsForm as FormValues, validationSchema } from './form';

import css from './SpecialDropSettingsForm.module.css';

interface SpecialDropSettingsFormProps {
  initialValues: FormValues;
  isSubmitting: boolean;
  onSubmit: (values: FormValues) => void;
}

export const SpecialDropSettingsForm: FC<SpecialDropSettingsFormProps> = ({
  initialValues,
  isSubmitting,
  onSubmit,
}) => {
  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, dirty } = formik;
  const { secondSpinOptions } = values;

  return (
    <form className={css.specialDropSettingsForm} onSubmit={handleSubmit}>
      <TextField
        name="minBalanceRewardAmount"
        label="Min balance reward amount"
        type="number"
        value={values.minBalanceRewardAmount}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched.minBalanceRewardAmount && errors.minBalanceRewardAmount)}
        helperText={touched.minBalanceRewardAmount && errors.minBalanceRewardAmount}
        required
      />

      <TextField
        name="maxBalanceRewardAmount"
        label="Max balance reward amount"
        type="number"
        value={values.maxBalanceRewardAmount}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched.maxBalanceRewardAmount && errors.maxBalanceRewardAmount)}
        helperText={touched.maxBalanceRewardAmount && errors.maxBalanceRewardAmount}
        required
      />

      <div className={css.secondSpinSection}>
        <span className={css.secondSpinSectionTitle}>Second Spin</span>

        <FormControlLabel
          control={
            <Checkbox
              name="secondSpinOptions.isEnabled"
              checked={secondSpinOptions.isEnabled}
              onChange={handleChange}
            />
          }
          label="Second Spin Enabled"
        />

        <TextField
          name="secondSpinOptions.description"
          label="Description"
          value={secondSpinOptions.description}
          onChange={handleChange}
          onBlur={handleBlur}
          multiline
          minRows={3}
          maxRows={5}
          slotProps={{ htmlInput: { maxLength: SECOND_SPIN_DESCRIPTION_MAX_LENGTH } }}
        />

        <TextField
          name="secondSpinOptions.buttonName"
          label="Button Value"
          value={secondSpinOptions.buttonName}
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <TextField
          name="secondSpinOptions.link"
          label="Button Link"
          value={secondSpinOptions.link}
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <TextField
          name="secondSpinOptions.offersTarget"
          label="Offers Target"
          type="number"
          value={secondSpinOptions.offersTarget}
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <TextField
          name="secondSpinOptions.offersCounter"
          label="Offers Counter (how much sent)"
          type="number"
          value={secondSpinOptions.offersCounter}
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <TextField
          name="secondSpinOptions.usedOffersCounter"
          label="Used Offers Counter (how much opened link)"
          type="number"
          value={secondSpinOptions.usedOffersCounter}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>

      <Button type="submit" variant="contained" loading={isSubmitting} disabled={!dirty}>
        Update
      </Button>
    </form>
  );
};
